// ============================================================================
// FITNESS FETCHER SERVICE
// TypeScript port for fetching athlete data from Intervals.icu
// ============================================================================

import { intervalsClient } from './intervals-client';
import type { IntervalsActivity } from '@/types';
import type { IntervalsWellness } from './intervals-client';
import { useStore } from '@/lib/store';
import { createLogger } from '@/lib/logger';

const log = createLogger('fitness-fetcher');

// ============================================================================
// TYPES
// ============================================================================

export interface FitnessData {
    ctl: number;  // Chronic Training Load (fitness)
    atl: number;  // Acute Training Load (fatigue)
    tsb: number;  // Training Stress Balance (form)
    rampRate: number;
}

export interface BiometricsData {
    rhr: { current: number | null; avg: number };
    hrv: { current: number | null; avg: number };
    sleep: { current: number | null; avg: number };  // sleep_quality (0-10)
    sleepHours: { current: number | null; avg: number };  // Actual sleep duration in hours
    soreness: { current: number | null; avg: number };
    weight?: number;
}

export interface WeeklyVolumeData {
    weekStart: string;
    weekEnd: string;
    totalKm: number;
    activities: Array<{
        date: string;
        name: string;
        distanceKm: number;
        type: string;
    }>;
}

export interface ComplianceData {
    compliance: number;
    plannedVolume: number;
    actualVolume: number;
    details: Array<{
        date: string;
        name: string;
        distance: string;
        type: string;
    }>;
}

export interface AthleteSettings {
    ftp?: number;
    lthr?: number;
    maxHr?: number;
    weight?: number;
    name?: string;
    profilePicture?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
}

function getDateRange(daysBack: number): { oldest: string; newest: string } {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - daysBack);
    return {
        oldest: formatDate(start),
        newest: formatDate(end),
    };
}

function calculateAverage(data: number[]): number {
    if (data.length === 0) return 0;
    const sum = data.reduce((acc, val) => acc + val, 0);
    return Math.round(sum / data.length);
}

// ============================================================================
// FITNESS FETCHER CLASS
// ============================================================================

class FitnessFetcher {
    private isCoachingContext(): boolean {
        const state = useStore.getState();
        const authUserId = state.authUser?.id;
        if (!authUserId) return false;

        if (
            state.isImpersonating &&
            state.impersonatedUserId &&
            state.impersonatedUserId !== authUserId
        ) {
            return true;
        }

        const activeProfile = state.profiles.find((profile) => profile.id === state.activeProfileId);
        return Boolean(activeProfile?.relation === 'coached' && activeProfile.id !== authUserId);
    }

    /**
     * Fetch athlete settings from Intervals.icu
     */
    async fetchAthleteSettings(): Promise<AthleteSettings | null> {
        if (this.isCoachingContext()) {
            log.info('fetchSettings', 'Skipping direct Intervals athlete settings fetch while coaching/impersonating');
            return null;
        }

        try {
            const data = await intervalsClient.getAthlete();

            const settings: AthleteSettings = {
                name: data.name,
                ftp: data.ftp,
                maxHr: data.max_hr,
                weight: data.weight,
                profilePicture: data.profile_medium,
            };

            // LTHR is in icu_lthr field
            if (data.icu_lthr) {
                settings.lthr = data.icu_lthr;
            }

            log.debug('fetchSettings', 'Athlete settings fetched', { settings });
            return settings;
        } catch (error) {
            log.error('fetchSettings', 'Athlete settings fetch error', { error: error instanceof Error ? error.message : String(error) });
            return null;
        }
    }

    /**
     * Fetch fitness metrics (CTL, ATL, TSB)
     */
    async fetchFitness(): Promise<FitnessData | null> {
        const consent = useStore.getState().consent;
        if (!consent.health) {
            log.warn('fetchFitness', 'Skipping fitness fetch: health consent not granted');
            return null;
        }
        if (this.isCoachingContext()) {
            log.info('fetchFitness', 'Skipping direct Intervals fitness fetch while coaching/impersonating');
            return null;
        }

        try {
            // Priority 1: Recent Wellness Data
            // This contains the day-by-day calculations and is typically what powers
            // the main "Current Fitness" chart and header on the Intervals.icu site.
            const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
            const start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            const end = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // Look a bit ahead for timezones

            const wellness = await intervalsClient.getWellness(start, end);

            if (wellness && wellness.length > 0) {
                // Sort by date descending to get the most recent first
                const sorted = [...wellness].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

                // Find the entry for exactly today first
                let latest = sorted.find(w => w.date === today && (w.ctl !== undefined || w.icu_ctl !== undefined));

                // If no entry for today, find the most recent entry with data
                if (!latest) {
                    latest = sorted.find(w => w.ctl !== undefined || w.icu_ctl !== undefined || w.atl !== undefined || w.icu_atl !== undefined);
                }

                if (latest) {
                    const ctl = latest.ctl ?? latest.icu_ctl ?? 0;
                    const atl = latest.atl ?? latest.icu_atl ?? 0;

                    // Intervals TSB can be percentage or absolute depending on user settings.
                    // We prefer absolute for our widget. TSB = CTL - ATL.
                    const tsb = ctl - atl;

                    const fitness: FitnessData = {
                        ctl,
                        atl,
                        tsb,
                        rampRate: 0,
                    };

                    log.debug('fetchFitness', 'Resolved from wellness', { date: latest.date, fitness });
                    return fitness;
                }
            }

            // Priority 2: Athlete Profile (Fallback)
            const athlete = await intervalsClient.getAthlete();
            if (athlete && (athlete.icu_ctl !== undefined || athlete.icu_atl !== undefined)) {
                const ctl = athlete.icu_ctl ?? 0;
                const atl = athlete.icu_atl ?? 0;
                const fitness: FitnessData = {
                    ctl,
                    atl,
                    tsb: athlete.icu_tsb ?? (ctl - atl),
                    rampRate: 0,
                };
                log.debug('fetchFitness', 'Resolved from athlete profile fallback', { fitness });
                return fitness;
            }

            return null;
        } catch (error) {
            log.error('fetchFitness', 'Fitness fetch error', { error: error instanceof Error ? error.message : String(error) });
            return null;
        }
    }

    /**
     * Fetch recent activities
     */
    async fetchActivities(daysBack = 40): Promise<IntervalsActivity[]> {
        const { oldest, newest } = getDateRange(daysBack);
        if (this.isCoachingContext()) {
            log.info('fetchActivities', 'Skipping direct Intervals activity fetch while coaching/impersonating');
            return [];
        }

        try {
            const activities = await intervalsClient.getActivities(oldest, newest);
            log.debug('fetchActivities', 'Fetched activities', { count: activities.length });
            return activities;
        } catch (error) {
            log.error('fetchActivities', 'Activities fetch error', { error: error instanceof Error ? error.message : String(error) });
            return [];
        }
    }

    /**
     * Fetch activities for a specific date range
     */
    async fetchActivitiesForRange(
        oldest: string,
        newest: string
    ): Promise<IntervalsActivity[]> {
        if (this.isCoachingContext()) {
            log.info('fetchActivities', 'Skipping direct Intervals range fetch while coaching/impersonating');
            return [];
        }

        try {
            return await intervalsClient.getActivities(oldest, newest);
        } catch (error) {
            log.error('fetchActivities', 'Activities range fetch error', { error: error instanceof Error ? error.message : String(error) });
            return [];
        }
    }

    /**
     * Fetch wellness data
     */
    async fetchWellness(daysBack = 28): Promise<IntervalsWellness[]> {
        const consent = useStore.getState().consent;
        if (!consent.health) {
            log.warn('fetchWellness', 'Skipping wellness fetch: health consent not granted');
            return [];
        }
        if (this.isCoachingContext()) {
            log.info('fetchWellness', 'Skipping direct Intervals wellness fetch while coaching/impersonating');
            return [];
        }

        const { oldest, newest } = getDateRange(daysBack);

        try {
            const wellness = await intervalsClient.getWellness(oldest, newest);
            log.debug('fetchWellness', 'Fetched wellness entries', { count: wellness.length });
            return wellness;
        } catch (error) {
            log.error('fetchWellness', 'Wellness fetch error', { error: error instanceof Error ? error.message : String(error) });
            return [];
        }
    }

    /**
     * Calculate biometrics from wellness data
     */
    calculateBiometrics(wellness: IntervalsWellness[]): BiometricsData | null {
        if (!wellness.length) return null;

        // Sort by date descending
        const sorted = [...wellness].sort((a, b) => {
            const dateA = a.date || (a as IntervalsWellness & { id?: string }).id || '';
            const dateB = b.date || (b as IntervalsWellness & { id?: string }).id || '';
            return new Date(dateB).getTime() - new Date(dateA).getTime();
        });

        const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
        const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('en-CA');
        const twoDaysAgo = new Date(Date.now() - (2 * 86400000)).toLocaleDateString('en-CA');
        const recentDates = [today, yesterday, twoDaysAgo];

        // Robust key lookup helper (greedy match)
        const getVal = (w: IntervalsWellness, keys: (keyof IntervalsWellness)[]) => {
            for (const key of keys) {
                if (w[key] !== undefined && w[key] !== null) return w[key];
            }
            return null;
        };

        const rhrKeys: (keyof IntervalsWellness)[] = ['rhr', 'restingHR'];
        const hrvKeys: (keyof IntervalsWellness)[] = ['hrv', 'hrvSDNN'];
        const sleepQualKeys: (keyof IntervalsWellness)[] = ['sleep_quality', 'sleepQuality'];
        const sleepSecsKeys: (keyof IntervalsWellness)[] = ['sleep_secs', 'sleepSecs'];
        const sorenessKeys: (keyof IntervalsWellness)[] = ['soreness'];

        // Find most recent for each metric independently within 3-day window
        const findRecentVal = (keys: (keyof IntervalsWellness)[]) => {
            const entry = sorted.find(w => {
                const date = w.date || (w as IntervalsWellness & { id?: string }).id || '';
                return recentDates.includes(date) && getVal(w, keys) !== null;
            });
            return entry ? getVal(entry, keys) : null;
        };

        const currentRhr = findRecentVal(rhrKeys) as number | null;
        const currentHrv = findRecentVal(hrvKeys) as number | null;
        const currentSleepQual = findRecentVal(sleepQualKeys) as number | null;
        const currentSleepSecs = findRecentVal(sleepSecsKeys) as number | null;
        const currentSoreness = findRecentVal(sorenessKeys) as number | null;

        // If no recent data at all, return null
        if (currentRhr === null && currentHrv === null && currentSleepQual === null && currentSleepSecs === null && currentSoreness === null) {
            log.debug('calculateBiometrics', 'No recent wellness data found in last 3 days');
            return null;
        }

        // Calculate averages (still based on all fetched data, usually 28 days)
        const extractValues = (keys: (keyof IntervalsWellness)[]) =>
            wellness.map(w => getVal(w, keys)).filter((v): v is number => v !== null && typeof v === 'number');

        const rhrValues = extractValues(rhrKeys);
        const hrvValues = extractValues(hrvKeys);
        const sorenessValues = extractValues(sorenessKeys);
        const sleepSecsValues = extractValues(sleepSecsKeys);
        const sleepQualValues = extractValues(sleepQualKeys);

        // Convert sleep_secs to hours
        const sleepHoursCurrent = currentSleepSecs !== null
            ? +(currentSleepSecs / 3600).toFixed(1)
            : null;
        const sleepHoursAvg = sleepSecsValues.length > 0
            ? +(sleepSecsValues.reduce((a, b: number) => a + b, 0) / sleepSecsValues.length / 3600).toFixed(1)
            : 0;

        return {
            rhr: {
                current: currentRhr,
                avg: calculateAverage(rhrValues),
            },
            hrv: {
                current: currentHrv,
                avg: calculateAverage(hrvValues),
            },
            sleep: {
                current: currentSleepQual,
                avg: calculateAverage(sleepQualValues),
            },
            sleepHours: {
                current: sleepHoursCurrent,
                avg: sleepHoursAvg,
            },
            soreness: {
                current: currentSoreness,
                avg: calculateAverage(sorenessValues),
            },
            weight: wellness.find(w => w.weight)?.weight,
        };
    }

    /**
     * @deprecated Use ActivityService and ActivityStore for standardized volume calculations.
     * Calculate weekly volume from activities
     */
    calculateWeeklyVolume(
        activities: IntervalsActivity[],
        sportType: 'Running' | 'Cycling' = 'Running'
    ): WeeklyVolumeData[] {
        const weeks: WeeklyVolumeData[] = [];
        const now = new Date();

        // Get last Sunday
        const lastSunday = new Date(now);
        lastSunday.setDate(now.getDate() - (now.getDay() === 0 ? 7 : now.getDay()));
        lastSunday.setHours(23, 59, 59, 999);

        // Calculate 4 weeks
        for (let i = 0; i < 4; i++) {
            const end = new Date(lastSunday);
            end.setDate(lastSunday.getDate() - (i * 7));

            const start = new Date(end);
            start.setDate(end.getDate() - 6);
            start.setHours(0, 0, 0, 0);

            const weekActivities: WeeklyVolumeData['activities'] = [];
            let totalKm = 0;

            activities.forEach(act => {
                const actDate = new Date(act.start_date_local);
                if (actDate >= start && actDate <= end) {
                    const actType = act.type?.toLowerCase() || '';
                    const isRun = actType.includes('run');
                    const isRide = actType.includes('ride') || actType.includes('cycling');

                    if ((sportType === 'Running' && isRun) || (sportType === 'Cycling' && isRide)) {
                        const distanceKm = (act.distance || 0) / 1000;
                        totalKm += distanceKm;
                        weekActivities.push({
                            date: act.start_date_local.split('T')[0],
                            name: act.name,
                            distanceKm,
                            type: act.type,
                        });
                    }
                }
            });

            weeks.push({
                weekStart: formatDate(start),
                weekEnd: formatDate(end),
                totalKm: Math.round(totalKm * 10) / 10,
                activities: weekActivities,
            });
        }

        return weeks;
    }

    /**
     * @deprecated Use ActivityService and ActivityStore for standardized compliance calculations.
     * Calculate compliance for a week
     */
    async calculateCompliance(
        weekStart: string,
        plannedVolume: number,
        sportType: 'Running' | 'Cycling' = 'Running'
    ): Promise<ComplianceData> {
        const startDate = new Date(weekStart);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 6);

        const oldest = weekStart;
        const newest = formatDate(endDate);

        const activities = await this.fetchActivitiesForRange(oldest, newest);

        let actualVolume = 0;
        const details: ComplianceData['details'] = [];

        activities.forEach(act => {
            const actType = act.type?.toLowerCase() || '';
            const isRun = actType.includes('run');
            const isRide = actType.includes('ride') || actType.includes('cycling');

            if ((sportType === 'Running' && isRun) || (sportType === 'Cycling' && isRide)) {
                const distanceKm = (act.distance || 0) / 1000;
                actualVolume += distanceKm;
                details.push({
                    date: act.start_date_local?.split('T')[0] || '',
                    name: act.name,
                    distance: distanceKm.toFixed(1),
                    type: act.type,
                });
            }
        });

        const compliance = plannedVolume > 0
            ? Math.min(actualVolume / plannedVolume, 1.5)
            : 1.0;

        return {
            compliance: Math.round(compliance * 100) / 100,
            plannedVolume: Math.round(plannedVolume * 10) / 10,
            actualVolume: Math.round(actualVolume * 10) / 10,
            details,
        };
    }

    /**
     * Get volume recommendations based on recent training
     */
    async getVolumeRecommendations(
        sportType: 'Running' | 'Cycling' = 'Running'
    ): Promise<{
        lastWeekVolume: number;
        avgVolume: number;
        conservative: number;
        moderate: number;
        aggressive: number;
    }> {
        const activities = await this.fetchActivities(28);
        const weeks = this.calculateWeeklyVolume(activities, sportType);

        const lastWeekVolume = weeks[0]?.totalKm || 0;
        const avgVolume = weeks.reduce((sum, w) => sum + w.totalKm, 0) / weeks.length;
        const baseVolume = lastWeekVolume > 0 ? lastWeekVolume : avgVolume;

        return {
            lastWeekVolume: Math.round(lastWeekVolume * 10) / 10,
            avgVolume: Math.round(avgVolume * 10) / 10,
            conservative: Math.round(baseVolume * 1.05 * 10) / 10,
            moderate: Math.round(baseVolume * 1.075 * 10) / 10,
            aggressive: Math.round(baseVolume * 1.10 * 10) / 10,
        };
    }
}

// Export singleton instance
export const fitnessFetcher = new FitnessFetcher();

// Export class for testing
export { FitnessFetcher };
